import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useContext, useMemo, useState } from "react";
import { Context } from "../../Reducers/Store";
import Loading from "../../components/Loading";

function Icon({ path }) {
  return (
    <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
    </svg>
  );
}

export default function LayoutDashboard() {
  const navigate = useNavigate();
  const [state, dispatch] = useContext(Context);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isAdmin = state.profile?.perfil === "Administrador";
  const nome = state.profile?.nome || state.profile?.name || "Usuário";
  const perfil = isAdmin ? "Administrador" : "Usuário";
  const iniciais = nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((item) => item[0])
    .join("")
    .toUpperCase() || "?";

  const navLinks = useMemo(() => {
    const items = [
      {
        to: "/dashboard",
        label: "Dashboard",
        icon: "M3 13h8V3H3v10Zm10 8h8v-6h-8v6ZM3 21h8v-6H3v6Zm10-8h8V3h-8v10Z",
      },
      {
        to: "/jobs",
        label: "Jobs",
        icon: "M10.5 6h9M10.5 12h9M10.5 18h9M4.5 6h.01M4.5 12h.01M4.5 18h.01",
      },
      {
        to: "/execucoes",
        label: "Execuções",
        icon: "M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
      },
    ];

    if (isAdmin) {
      items.push(
        { section: "Administração" },
        {
          to: "/autenticacoes",
          label: "Autenticações",
          icon: "M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.03 5.91l-1.34 1.34H10.5v2.25H8.25V21H4.5v-2.25L13.59 9.66a6 6 0 0 1 8.16-1.41Z",
        },
        {
          to: "/grupos-comunicacao-erro",
          label: "Grupos de falha",
          icon: "M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.24a2.25 2.25 0 0 1-1.07 1.91l-7.5 4.7a2.25 2.25 0 0 1-2.36 0l-7.5-4.7a2.25 2.25 0 0 1-1.07-1.91v-.24",
        },
        {
          to: "/usuarios",
          label: "Usuários",
          icon: "M15 19.13a4 4 0 0 0-6 0M17.25 7.5a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM21 21a8.25 8.25 0 0 0-16.5 0",
        }
      );
    }

    return items;
  }, [isAdmin]);

  const handleLogout = (event) => {
    event.preventDefault();
    localStorage.removeItem("jwt");
    localStorage.removeItem("refreshToken");
    dispatch({ type: "SET_LOGGED", payload: null });
    navigate("/login");
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-slate-950/50 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-64 shrink-0 transform flex-col transition-all duration-300 ease-in-out lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} ${sidebarCollapsed ? "lg:w-[90px]" : "lg:w-64"}`}
        style={{ background: "linear-gradient(160deg, #1e1b4b 0%, #3b0764 100%)" }}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-white/10 px-4">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-lg shadow-purple-950/30"
            style={{ background: "linear-gradient(135deg,#8b5cf6,#6d28d9)" }}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M13 10V3L4 14h7v7l9-11h-7Z" />
            </svg>
          </div>
          {!sidebarCollapsed && (
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-white">SMS Agendador</p>
              <p className="text-xs text-slate-400">Tarefas e jobs</p>
            </div>
          )}

          <button className="ml-auto text-purple-300 transition hover:text-white lg:hidden" onClick={() => setSidebarOpen(false)} title="Fechar menu">
            <Icon path="M6 18 18 6M6 6l12 12" />
          </button>
          <button
            className="ml-auto hidden rounded-md p-1 text-purple-400 transition hover:bg-white/10 hover:text-white lg:block"
            onClick={() => setSidebarCollapsed((value) => !value)}
            title={sidebarCollapsed ? "Expandir menu" : "Recolher menu"}
          >
            <Icon path={sidebarCollapsed ? "m9 18 6-6-6-6" : "m15 18-6-6 6-6"} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5">
          {!sidebarCollapsed && <p className="px-3 pb-2 text-xs font-semibold uppercase tracking-widest text-purple-400/50">Menu</p>}

          {navLinks.map((link, index) => {
            if (link.section) {
              if (sidebarCollapsed) return <div key={`section-${index}`} className="mx-3 my-3 h-px bg-white/10" />;
              return (
                <p key={`section-${index}`} className="px-3 pb-2 pt-4 text-[10px] font-semibold uppercase tracking-widest text-purple-400/50">
                  {link.section}
                </p>
              );
            }

            return (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setSidebarOpen(false)}
                title={sidebarCollapsed ? link.label : ""}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150 ${isActive ? "bg-violet-600 text-white shadow-lg shadow-violet-900/50" : "text-purple-200/60 hover:bg-white/10 hover:text-white"} ${sidebarCollapsed ? "justify-center" : ""}`
                }
              >
                <Icon path={link.icon} />
                {!sidebarCollapsed && <span className="truncate">{link.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="shrink-0 space-y-2 border-t border-white/10 p-3">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3 rounded-md px-2 py-2">
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
              >
                {iniciais}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{nome}</p>
                <p className="text-xs text-purple-400">{perfil}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            title="Sair"
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-400 transition-all hover:bg-red-500/10 hover:text-red-300 ${sidebarCollapsed ? "justify-center" : ""}`}
          >
            <Icon path="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6A2.25 2.25 0 0 0 5.25 5.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
            {!sidebarCollapsed && <span>Sair</span>}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6">
          <button className="text-slate-500 transition hover:text-slate-900 lg:hidden" onClick={() => setSidebarOpen(true)} title="Abrir menu">
            <Icon path="M4 6h16M4 12h16M4 18h16" />
          </button>

          <div className="hidden items-center gap-2 text-sm text-slate-500 lg:flex">
            <span className="text-violet-600"><Icon path="M13 10V3L4 14h7v7l9-11h-7Z" /></span>
            <span>Orquestrador de chamadas</span>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-semibold text-slate-800">{nome}</p>
              <p className="text-xs text-slate-400">{perfil}</p>
            </div>
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ background: "linear-gradient(135deg,#a78bfa,#7c3aed)" }}
            >
              {iniciais}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <Loading />
    </div>
  );
}
